import React, { useLayoutEffect, useRef, useState } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import { Wrapper, MeasureRow, Category, CategoryLabel, MoreCategory } from './styled';

const GAP = 8; // must match the `gap` set on Wrapper/MeasureRow

const Categories = ({ data }) => {
  const wrapperRef = useRef(null);
  const measureRefs = useRef([]);
  const moreRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(data?.length || 0);

  useLayoutEffect(() => {
    const wrapperEl = wrapperRef.current;
    if (!data?.length || !wrapperEl) return undefined;

    const measure = () => {
      if (!moreRef.current) return;

      const available = wrapperEl.clientWidth;
      const chips = measureRefs.current.slice(0, data.length);
      if (chips.some((el) => !el)) return;

      const totalWidth = chips.reduce((sum, el, i) => sum + el.offsetWidth + (i > 0 ? GAP : 0), 0);
      if (totalWidth <= available) {
        setVisibleCount(data.length);
        return;
      }

      // Real measured width of the "+N" chip (worst case: as many digits as there are
      // categories), not a guessed constant — a guessed reserve that runs bigger than the
      // real chip hides categories that would actually have fit.
      const moreWidth = moreRef.current.offsetWidth;

      let used = 0;
      let count = 0;
      for (let i = 0; i < chips.length; i += 1) {
        // chipWidth already includes the gap to the PREVIOUS chip (i > 0 ? GAP : 0); the
        // trailing "+ GAP" below is a different gap — the one between this chip and the
        // "+N" badge that would follow it if we stopped here. Both are needed together.
        const chipWidth = chips[i].offsetWidth + (i > 0 ? GAP : 0);
        if (used + chipWidth + GAP + moreWidth > available) break;
        used += chipWidth;
        count += 1;
      }
      // count can legitimately be 0 (not even one chip fits alongside a "+N" badge) — in
      // that case show only the "+N" badge for every category instead of forcing a chip
      // that would render clipped with no overflow indicator.
      setVisibleCount(count);
    };

    measure();

    // jsdom (and some older browsers) don't implement ResizeObserver — fall back to a
    // single synchronous measurement instead of crashing on mount.
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(wrapperEl);

    return () => observer.disconnect();
  }, [data]);

  if (!data?.length) return null;

  const hidden = data.slice(visibleCount);

  return (
    <Wrapper ref={ wrapperRef }>
      { /* Off-screen, always-full-width copy used only to measure real chip widths — the
         visible chips below get trimmed from this, so a resize never measures an
         already-hidden (0-width) chip. Includes a "+N" probe so its reserve is measured,
         not guessed. */ }
      <MeasureRow aria-hidden="true">
        { data.map((tag, i) => (
          <Category key={ tag } ref={ (el) => { measureRefs.current[i] = el; } }><CategoryLabel>{ tag }</CategoryLabel></Category>
        )) }
        <MoreCategory ref={ moreRef }><CategoryLabel>{ `+${data.length}` }</CategoryLabel></MoreCategory>
      </MeasureRow>
      { data.slice(0, visibleCount).map((tag) => (
        <Category key={ tag } title={ tag }><CategoryLabel>{ tag }</CategoryLabel></Category>
      )) }
      { hidden.length > 0 &&
        <Tooltip title={ hidden.join(', ') }>
          <MoreCategory tabIndex={ 0 }><CategoryLabel>{ `+${hidden.length}` }</CategoryLabel></MoreCategory>
        </Tooltip>
      }
    </Wrapper>
  );
};

export default Categories;
